"use client";

import { useState } from "react";
import { ChevronDown } from "@/components/icons";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { InlineCombobox } from "@/components/ui/inline-combobox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { isOneOf } from "@shared/validation/is-one-of";
import { needsLiftgate } from "@shared/freight/site-access-service-options";
import { literalValues } from "@shared/validation/literal-values";
import {
  LIMITED_ACCESS_KIND,
  SITE_INSIDE,
  SITE_PLACE,
  SITE_STOP,
  type SiteStop,
} from "@convex/constants/siteAccess.lib";
import type { SiteAccess } from "@convex/schemas/validators/siteAccess.lib";
import type { FreightVehicle } from "@convex/schemas/validators/freightShipment.lib";
import { INSIDE_OPTIONS, LIMITED_ACCESS_OPTIONS, PLACE_OPTIONS, SITE_VEHICLE_OPTIONS, moreOptionsCount } from "./site-access-options";

export interface SiteAccessFieldsProps {
  value: SiteAccess | undefined;
  onChange: (value: SiteAccess) => void;
  stop?: SiteStop;
  disabled?: boolean;
  idPrefix?: string;
  className?: string;
}

type Flag = "dock" | "forklift" | "appointment" | "palletJack" | "driverAssist" | "dropTrailer" | "lumper";

interface FieldContext {
  access: SiteAccess;
  set: (patch: Partial<SiteAccess>) => void;
  disabled: boolean;
  id: (name: string) => string;
}

function FlagCheckbox({ ctx, flag, label, hint, status }: { ctx: FieldContext; flag: Flag; label: string; hint?: string; status?: string }) {
  const id = ctx.id(flag);
  return (
    <label htmlFor={id} className="flex min-h-11 cursor-pointer items-start gap-2.5 py-1.5 text-sm">
      <Checkbox
        id={id}
        className="mt-0.5"
        checked={ctx.access[flag] === true}
        disabled={ctx.disabled}
        onCheckedChange={(checked) => ctx.set({ [flag]: checked === true })}
      />
      <span className="grid gap-0.5">
        <span className="text-foreground">{label}</span>
        {status ? <span className="text-sm text-foreground">{status}</span> : null}
        {!status && hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      </span>
    </label>
  );
}

function ResidenceCheckbox({ ctx, stop }: { ctx: FieldContext; stop: SiteStop }) {
  const id = ctx.id("residence");
  const checked = ctx.access.place === SITE_PLACE.RESIDENCE;
  return (
    <label htmlFor={id} className="flex min-h-11 cursor-pointer items-start gap-2.5 py-1.5 text-sm">
      <Checkbox
        id={id}
        className="mt-0.5"
        checked={checked}
        disabled={ctx.disabled}
        onCheckedChange={(next) => ctx.set({ place: next === true ? SITE_PLACE.RESIDENCE : SITE_PLACE.BUSINESS, limitedAccessKind: undefined })}
      />
      <span className="grid gap-0.5">
        <span className="text-foreground">Home or home-based business</span>
        <span className="text-xs text-muted-foreground">{stop === SITE_STOP.PICKUP ? "Picked up from a house or apartment" : "Delivered to a house or apartment"}</span>
      </span>
    </label>
  );
}

function SelectField({ ctx, name, label, value, options, placeholder, onSelect }: {
  ctx: FieldContext;
  name: string;
  label: string;
  value: string | undefined;
  options: ReadonlyArray<{ value: string; label: string }>;
  placeholder: string;
  onSelect: (value: string) => void;
}) {
  const id = ctx.id(name);
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <InlineCombobox id={id} variant="field" ariaLabel={label} value={value ?? null} options={[...options]} onSelect={onSelect} placeholder={placeholder} disabled={ctx.disabled} />
    </div>
  );
}

function PlaceFields({ ctx }: { ctx: FieldContext }) {
  const { access } = ctx;
  if (access.place === SITE_PLACE.RESIDENCE) return null;
  return (
    <div className="grid gap-4 @md:grid-cols-2">
      <SelectField ctx={ctx} name="place" label="Kind of place" value={access.place ?? SITE_PLACE.BUSINESS} options={PLACE_OPTIONS} placeholder="Business"
        onSelect={(next) => { if (isOneOf(literalValues(SITE_PLACE), next)) ctx.set({ place: next, limitedAccessKind: undefined }); }} />
      {access.place === SITE_PLACE.LIMITED_ACCESS ? (
        <div className="@md:border-l-0 border-l-2 border-border pl-3 @md:pl-0"><SelectField ctx={ctx} name="limited-kind" label="What kind?" value={access.limitedAccessKind} options={LIMITED_ACCESS_OPTIONS} placeholder="Choose one"
          onSelect={(next) => { if (isOneOf(literalValues(LIMITED_ACCESS_KIND), next)) ctx.set({ limitedAccessKind: next }); }} /></div>
      ) : null}
    </div>
  );
}

function VehicleFields({ ctx }: { ctx: FieldContext }) {
  const chosen = ctx.access.vehicles ?? [];
  const [onlySome, setOnlySome] = useState(chosen.length > 0);
  const toggle = (vehicle: FreightVehicle, checked: boolean) => {
    const next = checked ? [...chosen, vehicle] : chosen.filter((item) => item !== vehicle);
    ctx.set({ vehicles: next.length > 0 ? next : undefined });
  };
  const choose = (mode: string) => {
    setOnlySome(mode === "only");
    if (mode === "any") ctx.set({ vehicles: undefined });
  };
  return (
    <fieldset className="grid gap-1">
      <legend className="text-sm font-medium text-foreground">Which trucks can get in?</legend>
      <RadioGroup value={onlySome ? "only" : "any"} onValueChange={choose} disabled={ctx.disabled} orientation="horizontal" className="min-h-11">
        {(["any", "only"] as const).map((mode) => {
          const id = ctx.id(`vehicles-${mode}`);
          return (
            <label key={mode} htmlFor={id} className="flex min-h-11 cursor-pointer items-center gap-2.5 text-sm">
              <RadioGroupItem id={id} value={mode} />
              {mode === "any" ? "Any truck" : "Only these trucks"}
            </label>
          );
        })}
      </RadioGroup>
      {onlySome ? (
        <div className="grid grid-cols-2 gap-x-6 pl-6 @lg:grid-cols-4">
          {SITE_VEHICLE_OPTIONS.map((option) => {
            const id = ctx.id(`vehicle-${option.value}`);
            return (
              <label key={option.value} htmlFor={id} className="flex min-h-11 cursor-pointer items-center gap-2.5 text-sm">
                <Checkbox id={id} checked={chosen.includes(option.value)} disabled={ctx.disabled} onCheckedChange={(checked) => toggle(option.value, checked === true)} />
                {option.label}
              </label>
            );
          })}
        </div>
      ) : null}
      {onlySome && chosen.length === 0 ? <p className="pl-6 text-xs text-muted-foreground">Tick at least one, or any truck is assumed.</p> : null}
    </fieldset>
  );
}

function MoreOptions({ ctx, stop }: { ctx: FieldContext; stop: SiteStop }) {
  const count = moreOptionsCount(ctx.access, stop);
  const [open, setOpen] = useState(count > 0);
  const insideLabel = stop === SITE_STOP.PICKUP ? "Driver goes inside to pick up" : "Driver brings it inside";
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex min-h-11 items-center gap-1.5 text-sm font-medium text-action hover:underline" disabled={ctx.disabled}>
        <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} aria-hidden />
        {open ? "Fewer options" : count > 0 ? `More options (${count} set)` : "More options"}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 grid gap-4 border-t border-border pt-4">
        <PlaceFields ctx={ctx} />
        {stop === SITE_STOP.DELIVERY ? <VehicleFields ctx={ctx} /> : null}
        <SelectField ctx={ctx} name="inside" label={insideLabel} value={ctx.access.inside ?? SITE_INSIDE.NONE} options={INSIDE_OPTIONS} placeholder="No"
          onSelect={(next) => { if (isOneOf(literalValues(SITE_INSIDE), next)) ctx.set({ inside: next }); }} />
        <div className="grid gap-x-6 @md:grid-cols-2">
          <FlagCheckbox ctx={ctx} flag="palletJack" label="Pallet jack on site" />
          <FlagCheckbox ctx={ctx} flag="driverAssist" label={stop === SITE_STOP.PICKUP ? "Driver helps load" : "Driver helps unload"} hint="With a pallet jack" />
          <FlagCheckbox ctx={ctx} flag="dropTrailer" label="Can take a dropped trailer" hint="Drop and hook" />
          {stop === SITE_STOP.DELIVERY ? <FlagCheckbox ctx={ctx} flag="lumper" label="Receiver requires a lumper" hint="Paid unloading crew" /> : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function liftgateStatus(access: SiteAccess): string | undefined {
  return access.dock === false && needsLiftgate(access) ? "The truck comes with a liftgate" : undefined;
}

export function SiteAccessFields({ value, onChange, stop = SITE_STOP.DELIVERY, disabled = false, idPrefix = "site-access", className }: SiteAccessFieldsProps) {
  const access = value ?? {};
  const ctx: FieldContext = {
    access,
    disabled,
    set: (patch) => onChange({ ...access, ...patch }),
    id: (name) => `${idPrefix}-${name}`,
  };
  return (
    <div className={cn("@container grid gap-2", className)}>
      <div className="grid gap-x-6 @md:grid-cols-2">
        <FlagCheckbox ctx={ctx} flag="dock" label="Loading dock" hint="Trucks back up to a dock door" status={liftgateStatus(access)} />
        <FlagCheckbox ctx={ctx} flag="forklift" label="Forklift on site" />
        <ResidenceCheckbox ctx={ctx} stop={stop} />
        <FlagCheckbox ctx={ctx} flag="appointment" label="Appointment required" hint="Drivers call ahead to book a time" />
      </div>
      {stop === SITE_STOP.PICKUP ? <VehicleFields ctx={ctx} /> : null}
      <MoreOptions ctx={ctx} stop={stop} />
    </div>
  );
}
